import {
  createContext,
  useContext,
  useMemo,
  type JSX,
  type ReactNode,
} from 'react';

import { getMemberIdWithFallback } from '@trello/authentication';
import { isDesktop } from '@trello/browser';
import {
  useCrossFlowWorkspaces,
  useHasEligibleWorkspaces,
} from '@trello/cross-flow';
import { useWorkspace } from '@trello/workspaces';

type CrossFlowPostOfficeContextType = {
  memberId: string | null;
  hasPaidWorkspace: boolean | null;
  isWeb: boolean | null;
};

export const CrossFlowPostOfficeContext =
  createContext<CrossFlowPostOfficeContextType>({
    memberId: null,
    hasPaidWorkspace: null,
    isWeb: null,
  });

export const CrossFlowPostOfficeProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const memberId = getMemberIdWithFallback();
  const { workspaceId } = useWorkspace();
  const isWeb = !isDesktop();
  // TODO go/j/6896 Fetch workspaces from Post Office
  const workspaces = useCrossFlowWorkspaces();
  const { isEligible: hasPaidWorkspace } = useHasEligibleWorkspaces({
    workspaceId: workspaceId || '',
    entitlementRequired: 'standardOrPremium',
    treatFreeTrialAsFree: true,
    workspaces,
  });

  const context = useMemo(() => {
    return {
      memberId,
      hasPaidWorkspace,
      isWeb,
    };
  }, [memberId, hasPaidWorkspace, isWeb]);

  return (
    <CrossFlowPostOfficeContext.Provider value={context}>
      {children}
    </CrossFlowPostOfficeContext.Provider>
  );
};

export const useCrossFlowPostOfficeContext =
  (): CrossFlowPostOfficeContextType => useContext(CrossFlowPostOfficeContext);
